import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Trophy, Star, Gift, ShoppingBag, History, ChevronRight, Loader2, Lock, Check, Flame, Zap, Crown, Target, Heart, Award, Diamond, DollarSign, Ticket, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ACHIEVEMENT_ICONS = {
    trophy: Trophy, star: Star, ticket: Ticket, zap: Zap, flame: Flame,
    crown: Crown, dollar: DollarSign, diamond: Diamond, heart: Heart,
    map: MapPin, target: Target, award: Award,
};

const Rewards = () => {
    const { user, refreshUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [shop, setShop] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [redeemLoading, setRedeemLoading] = useState(null);
    const redeemBusy = useRef(false);

    const fetchProfile = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/rewards/profile`);
            setProfile(data);
        } catch {}
    }, []);

    const fetchShop = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/rewards/shop`);
            setShop(data);
        } catch {}
    }, []);

    const fetchHistory = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/rewards/history`);
            setHistory(data);
        } catch {}
    }, []);

    useEffect(() => {
        const load = async () => {
            await Promise.all([fetchProfile(), fetchShop(), fetchHistory()]);
            setLoading(false);
        };
        load();
    }, [fetchProfile, fetchShop, fetchHistory]);

    const handleRedeem = async (itemId) => {
        if (redeemBusy.current) return;
        redeemBusy.current = true;
        setRedeemLoading(itemId);
        try {
            const { data } = await axios.post(`${API}/rewards/redeem`, { item_id: itemId });
            toast.success(data.message);
            fetchProfile();
            fetchShop();
            fetchHistory();
            if (refreshUser) refreshUser();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to redeem');
        }
        setRedeemLoading(null);
        redeemBusy.current = false;
    };

    if (loading) {
        return (
            <div className="a11y-font min-h-screen bg-[#050505]">
                <Header />
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
                </div>
            </div>
        );
    }

    const tier = profile?.tier;
    const nextTier = profile?.next_tier;
    const progress = profile?.progress || 0;

    return (
        <div className="a11y-font min-h-screen bg-[#050505]">
            <Header />
            <div className="max-w-2xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="text-center mb-4">
                    <h1 className="text-3xl font-black text-white">
                        <span className="text-[#FFD700]">REWARDS</span>
                    </h1>
                    <p className="text-[#A0AAB2] text-sm">Earn points. Unlock perks. Get rewarded.</p>
                </div>

                {/* Points & Tier Card */}
                <div className="card-dark p-5 mb-4 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10" style={{ background: tier?.color, filter: 'blur(40px)' }} />
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <p className="text-xs text-[#A0AAB2]">YOUR POINTS</p>
                            <p className="text-3xl font-black font-mono" style={{ color: tier?.color }}>{(profile?.points || 0).toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                            <span className="inline-block px-3 py-1 rounded-full text-xs font-black" style={{ backgroundColor: tier?.color + '30', color: tier?.color }}>
                                {tier?.name}
                            </span>
                            <p className="text-[10px] text-[#A0AAB2] mt-1">Lifetime: {(profile?.lifetime_points || 0).toLocaleString()}</p>
                        </div>
                    </div>
                    {nextTier && (
                        <div>
                            <div className="flex items-center justify-between text-[10px] mb-1">
                                <span style={{ color: tier?.color }}>{tier?.name}</span>
                                <span className="text-[#A0AAB2]">{nextTier.min_points - (profile?.lifetime_points || 0)} pts to {nextTier.name}</span>
                                <span style={{ color: nextTier.color }}>{nextTier.name}</span>
                            </div>
                            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                                <motion.div initial={{ width: 0 }} animate={{ width: `${progress}%` }}
                                    className="h-full rounded-full" style={{ backgroundColor: tier?.color }} />
                            </div>
                        </div>
                    )}
                    {tier?.perks && (
                        <div className="mt-3 flex flex-wrap gap-1">
                            {tier.perks.map((p, i) => (
                                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#A0AAB2]">{p}</span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="flex gap-1 mb-4">
                    {[
                        { id: 'overview', label: 'Badges', icon: Trophy },
                        { id: 'shop', label: 'Shop', icon: ShoppingBag },
                        { id: 'history', label: 'History', icon: History },
                    ].map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1 ${
                                tab === t.id ? 'bg-[#FFD700] text-black' : 'bg-white/5 text-[#A0AAB2]'
                            }`} data-testid={`rewards-tab-${t.id}`}>
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>

                {/* Badges Tab */}
                {tab === 'overview' && (
                    <div className="space-y-2">
                        <p className="text-sm text-[#A0AAB2] mb-1">{profile?.earned_count}/{profile?.total_achievements} Unlocked</p>
                        <div className="grid grid-cols-2 gap-2">
                            {(profile?.achievements || []).map(ach => {
                                const Icon = ACHIEVEMENT_ICONS[ach.icon] || Star;
                                return (
                                    <div key={ach.id}
                                        className={`card-dark p-3 transition-all ${ach.earned ? 'border-l-2' : 'opacity-40'}`}
                                        style={ach.earned ? { borderLeftColor: '#FFD700' } : {}}
                                        data-testid={`achievement-${ach.id}`}>
                                        <div className="flex items-center gap-2 mb-1">
                                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${ach.earned ? 'bg-[#FFD700]/20' : 'bg-white/5'}`}>
                                                {ach.earned ? <Icon className="w-4 h-4 text-[#FFD700]" /> : <Lock className="w-3 h-3 text-[#666]" />}
                                            </div>
                                            <div>
                                                <p className={`text-xs font-bold ${ach.earned ? 'text-white' : 'text-[#666]'}`}>{ach.name}</p>
                                                <p className="text-[10px] text-[#666]">+{ach.points} pts</p>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-[#A0AAB2]">{ach.desc}</p>
                                        {ach.earned && <Check className="w-3 h-3 text-[#39FF14] absolute top-2 right-2" />}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Shop Tab */}
                {tab === 'shop' && (
                    <div className="space-y-2">
                        <p className="text-sm text-[#A0AAB2] mb-1">Your Points: <span className="text-[#FFD700] font-bold">{(shop?.points || 0).toLocaleString()}</span></p>
                        {(shop?.items || []).map(item => {
                            const canAfford = (shop?.points || 0) >= item.cost;
                            return (
                                <div key={item.id} className="card-dark p-4 flex items-center justify-between" data-testid={`shop-item-${item.id}`}>
                                    <div>
                                        <p className="text-sm font-bold text-white">{item.name}</p>
                                        <p className="text-xs text-[#FFD700] font-mono">{item.cost} pts</p>
                                    </div>
                                    <Button size="sm" onClick={() => handleRedeem(item.id)}
                                        disabled={!canAfford || redeemLoading === item.id}
                                        className={canAfford ? 'bg-[#39FF14] text-black font-bold' : 'bg-white/10 text-[#666]'}
                                        data-testid={`redeem-${item.id}`}>
                                        {redeemLoading === item.id ? <Loader2 className="w-3 h-3 animate-spin" /> : canAfford ? 'Redeem' : 'Locked'}
                                    </Button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* History Tab */}
                {tab === 'history' && (
                    <div className="space-y-1">
                        {history.length === 0 ? (
                            <p className="text-[#A0AAB2] text-center py-8 text-sm">No points activity yet. Start playing to earn!</p>
                        ) : history.map((h, i) => (
                            <div key={i} className="card-dark p-3 flex items-center justify-between">
                                <div>
                                    <p className="text-xs text-white">{h.description || h.action}</p>
                                    <p className="text-[10px] text-[#666]">
                                        {new Date(h.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <span className={`font-mono font-bold text-sm ${h.points > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]'}`}>
                                    {h.points > 0 ? '+' : ''}{h.points}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Tier Overview */}
                <div className="mt-6 card-dark p-4">
                    <h3 className="text-sm font-bold text-white mb-3">All Tiers</h3>
                    <div className="space-y-2">
                        {[
                            { name: 'Bronze', color: '#CD7F32', min: 0 },
                            { name: 'Silver', color: '#C0C0C0', min: 500 },
                            { name: 'Gold', color: '#FFD700', min: 2000 },
                            { name: 'Platinum', color: '#E5E4E2', min: 5000 },
                            { name: 'Diamond', color: '#B9F2FF', min: 15000 },
                        ].map(t => (
                            <div key={t.name} className={`flex items-center justify-between py-1.5 ${
                                tier?.name === t.name ? 'opacity-100' : 'opacity-40'
                            }`}>
                                <div className="flex items-center gap-2">
                                    {tier?.name === t.name && <ChevronRight className="w-3 h-3" style={{ color: t.color }} />}
                                    <span className="text-sm font-bold" style={{ color: t.color }}>{t.name}</span>
                                </div>
                                <span className="text-xs text-[#A0AAB2] font-mono">{t.min.toLocaleString()} pts</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Rewards;
